import * as React from 'react'
import { View, FlatList } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import Header from '@components/header/Header'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import api from '@api/api'
import Logger from '@utils/logger'

const RELOAD_TIME = 10000
const CRON_QUARTZ = 'org.springframework.scheduling.quartz'

const AdminJobsScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const [triggers, setTriggers] = React.useState<QuartzInfo[]>([])
  const [updating, setUpdating] = React.useState<string[]>([])
  const [status, setStatus] = React.useState<'loading' | 'fetched'>('loading')

  const reload = () => {
    return api.quartz
      .all()
      .then((group) => Promise.all(Object.keys(group.jobs).map(api.quartz.info)))
      .then(setTriggers)
      .catch(Logger.error)
  }

  React.useEffect(() => {
    setStatus('loading')

    // api call
    reload().finally(() => setStatus('fetched'))

    // cyclic update
    const timer = setInterval(reload, RELOAD_TIME)
    return () => clearInterval(timer)
  }, [])

  React.useEffect(() => {
    // running if any trigger is not spring action
    const next = triggers.filter((t) => t.triggers.some((tr) => !tr.name.includes(CRON_QUARTZ))).map((t) => t.name)

    setUpdating(next)
  }, [triggers])

  const start = (name: string) => {
    api.quartz
      .start(name)
      .then(() => setUpdating((prev) => [...prev, name]))
      .catch(Logger.error)
  }

  const renderItem = ({ item }: { item: QuartzInfo }) => {
    return (
      <Item>
        <SectionTitle
          title={i18n.t(`screens.admin.jobs.${item.name}`)}
          button={{
            label: i18n.t('common.restart'),
            onClick: () => start(item.name),
            status: updating.includes(item.name) ? 'loading' : 'active',
          }}
        />
      </Item>
    )
  }

  return (
    <Root loading={status === 'loading'} header={i18n.t('screens.admin.jobs.title')} onLeft={navigation.goBack} fixed>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.admin.jobs.title')} />
        </HeaderContainer>
      )}

      <FlatList data={triggers} renderItem={renderItem} keyExtractor={(item) => item.name} extraData={updating} />
    </Root>
  )
}

export default AdminJobsScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const Item = styled(View)`
  margin: 0 24px;
  margin-top: 24px;
`
